import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

/**
 * Report-a-problem dialog. Surfaces the user's description to platform
 * admins via POST /api/alerts. We auto-attach the current pathname as
 * page_context; the server backfills `related_assistant_request_ids`
 * with the reporter's 5 most-recent assistant_requests so admins can
 * pull up the conversation that led to the report.
 *
 * Designed as a leaf component so it can drop into multiple surfaces
 * (AskEric widget, Messaging page, future error toasts) without each
 * caller needing to know the API shape.
 */

type ReportProblemDialogProps = {
  open: boolean
  onClose: () => void
  /** Optional override for page_context; defaults to current pathname. */
  pageContext?: string
  /**
   * Optional explicit assistant_request ids to associate. When omitted,
   * the server picks the user's 5 most recent. Useful when the caller
   * already has the precise context (e.g. the AskEric thread).
   */
  relatedAssistantRequestIds?: string[]
}

export function ReportProblemDialog({
  open,
  onClose,
  pageContext,
  relatedAssistantRequestIds,
}: ReportProblemDialogProps) {
  const { t } = useTranslation('report_problem')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const location = useLocation()
  const [description, setDescription] = useState('')
  const [submitted, setSubmitted] = useState(false)

  // Reset internal state when the dialog closes so the next open is
  // fresh. Doing it on close (not on open) keeps state changes out of
  // useEffect — which the linter rejects (react-hooks/set-state-in-effect)
  // — while still giving the user a clean form each time.
  const handleClose = () => {
    setDescription('')
    setSubmitted(false)
    onClose()
  }

  const report = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/alerts', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: description.trim(),
          page_context: pageContext ?? location.pathname,
          related_assistant_request_ids:
            relatedAssistantRequestIds && relatedAssistantRequestIds.length > 0
              ? relatedAssistantRequestIds
              : undefined,
        }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || t('error.generic'))
      }
      return res.json()
    },
    onSuccess: () => setSubmitted(true),
  })

  const submitting = report.isPending

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>
            {submitted ? t('success.body') : t('intro')}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            {t('success.confirmation')}
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="report-problem-description">
              {t('field_label')}
            </Label>
            <Textarea
              id="report-problem-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={4000}
              placeholder={t('placeholder')}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              {t('helper')}
            </p>
            {report.isError ? (
              <div className="text-xs text-destructive">
                {report.error instanceof Error
                  ? report.error.message
                  : t('error.generic')}
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={submitting}>
              {submitted ? t('done') : t('cancel')}
            </Button>
          </DialogClose>
          {!submitted ? (
            <Button
              type="button"
              onClick={() => report.mutate()}
              disabled={submitting || description.trim().length === 0}
            >
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('submitting')}
                </>
              ) : (
                t('submit')
              )}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
